import { useState } from 'react';
import styles from './ConversationHistory.module.css';

/**
 * ConversationHistory Component
 * Displays conversation history with edit, delete, and reorder functionality
 */
function ConversationHistory({ 
    messages = [], 
    onUpdate, 
    onClear,
    isCollapsed = true,
    onToggleCollapse 
}) {
    const [editingIndex, setEditingIndex] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);

    const handleEdit = (index) => {
        setEditingIndex(index);
        setEditContent(messages[index].content);
    };

    const handleSave = (index) => {
        const updated = [...messages];
        updated[index] = {
            ...updated[index],
            content: editContent,
            edited: true,
        };
        onUpdate(updated);
        setEditingIndex(null);
        setEditContent('');
    };

    const handleCancel = () => {
        setEditingIndex(null);
        setEditContent('');
    };

    const handleDelete = (index) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
            const updated = messages.filter((_, i) => i !== index);
            onUpdate(updated);
        }
    };

    const handleMoveUp = (index) => {
        if (index === 0) return;
        const updated = [...messages];
        [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
        onUpdate(updated);
    };

    const handleMoveDown = (index) => {
        if (index === messages.length - 1) return;
        const updated = [...messages];
        [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
        onUpdate(updated);
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const updated = [...messages];
        const dragged = updated[draggedIndex];
        updated.splice(draggedIndex, 1);
        updated.splice(dropIndex, 0, dragged);
        onUpdate(updated);
        setDraggedIndex(null);
    };

    if (isCollapsed) {
        return (
            <div className={styles.container}>
                <button 
                    onClick={onToggleCollapse}
                    className={styles.toggleButton}
                >
                    {messages.length > 0 ? `Show Conversation History (${messages.length} messages)` : 'Show Conversation History'}
                    <span className={styles.chevron}>▼</span>
                </button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button 
                    onClick={onToggleCollapse}
                    className={styles.toggleButton}
                >
                    Hide Conversation History
                    <span className={styles.chevron}>▲</span>
                </button>
                {messages.length > 0 && (
                    <button 
                        onClick={onClear}
                        className={styles.clearButton}
                    >
                        Clear History
                    </button>
                )}
            </div>
            
            {messages.length === 0 ? (
                <div className={styles.emptyState}>
                    No conversation history yet. Start a conversation to see messages here.
                </div>
            ) : (
                <div className={styles.messagesContainer}>
                    {messages.map((msg, index) => (
                        <div
                            key={index}
                            className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}
                            draggable={editingIndex === null}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                        >
                            <div className={styles.messageHeader}>
                                <span className={styles.role}>{msg.role === 'user' ? 'You' : 'Assistant'}</span>
                                {msg.edited && (
                                    <span className={styles.editedBadge} title="This message was edited">(edited)</span>
                                )}
                                {msg.timestamp && (
                                    <span className={styles.timestamp}>
                                        {new Date(msg.timestamp).toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                            
                            {editingIndex === index ? (
                                <div className={styles.editContainer}>
                                    <textarea
                                        value={editContent}
                                        onChange={(e) => setEditContent(e.target.value)}
                                        className={styles.editTextarea}
                                        rows={4}
                                    />
                                    <div className={styles.editActions}>
                                        <button 
                                            onClick={() => handleSave(index)}
                                            className={styles.saveButton}
                                        >
                                            Save
                                        </button>
                                        <button 
                                            onClick={handleCancel}
                                            className={styles.cancelButton}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.messageContent}>
                                        {msg.content}
                                    </div>
                                    {msg.metadata?.sources && msg.metadata.sources.length > 0 && (
                                        <div className={styles.sources}>
                                            <strong>Sources:</strong> {msg.metadata.sources.length}
                                        </div>
                                    )}
                                    <div className={styles.messageActions}>
                                        <button
                                            onClick={() => handleEdit(index)}
                                            className={styles.actionButton}
                                            title="Edit message"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className={styles.actionButton}
                                            title="Delete message"
                                        >
                                            🗑️
                                        </button>
                                        <button
                                            onClick={() => handleMoveUp(index)}
                                            disabled={index === 0}
                                            className={styles.actionButton}
                                            title="Move up"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            onClick={() => handleMoveDown(index)}
                                            disabled={index === messages.length - 1}
                                            className={styles.actionButton}
                                            title="Move down"
                                        >
                                            ↓
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ConversationHistory;
